/* eslint-disable no-console */
// Acceso a `liquidaciones_contadora` (tabla + Storage bucket `liquidaciones` + RPC
// `guardar_liquidacion_contadora`) — fuente única (10.1) consumida por la pantalla de Gestión de
// Empleados (PASO C2) y, en el futuro, por cualquier otra superficie. La RLS de la tabla/bucket
// (`es_admin_liquidaciones()` / `empleado_id = empleado_actual_id()`) ya gatea QUIÉN ve/escribe qué —
// estas funciones no reimplementan ese criterio, solo llaman a la base.
import { client } from "../supabase";

// TODAS las versiones (vigente + históricas, nunca se borran — append-only) de un empleado, más
// nuevas primero. Sirve tanto para el listado (filtra vigente=true del lado del caller) como para
// calcular el próximo número de versión al reemplazar un mes.
export const getLiquidacionesContadoraEmpleado = async (empleadoId) => {
  try {
    const { data, error } = await client
      .from("liquidaciones_contadora")
      .select("*")
      .eq("empleado_id", empleadoId)
      .order("periodo_año", { ascending: false })
      .order("periodo_mes", { ascending: false })
      .order("cargado_en", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getLiquidacionesContadoraEmpleado:", error);
    throw error;
  }
};

// TODAS las versiones (vigente + históricas) de VARIOS empleados en UNA sola query — para la carga
// masiva (botón general "Cargar liquidaciones"): antes de guardar, hay que saber por cada
// archivo/período si ya existe una vigente ("ya existe esa liquidación", informativo — la RPC igual
// la reemplaza por versión nueva, 10.202) y cuántas versiones previas tiene (para el número de
// versión del path de Storage). Un solo query batcheado, nunca N+1 por archivo (10.1, mismo criterio
// que getHaberesPais/getFechasPagoPais).
export const getLiquidacionesContadoraPorEmpleados = async (empleadoIds) => {
  if (!empleadoIds || empleadoIds.length === 0) return [];
  try {
    const { data, error } = await client
      .from("liquidaciones_contadora")
      .select("empleado_id, periodo_año, periodo_mes, vigente")
      .in("empleado_id", empleadoIds);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getLiquidacionesContadoraPorEmpleados:", error);
    throw error;
  }
};

// URL firmada de descarga (bucket privado — nunca público). Vence rápido a propósito (documento
// legal con datos previsionales/sueldo; no queda un link reusable dando vueltas).
export const getUrlLiquidacionPdf = async (archivoPath) => {
  const { data, error } = await client.storage.from("liquidaciones").createSignedUrl(archivoPath, 120);
  if (error) throw error;
  return data.signedUrl;
};

// Sube el PDF al path versionado (nunca pisa un archivo existente — el path lleva `v<n>`, ver
// Fase A §3/§4). El número de versión lo decide el caller (cuenta cuántas filas ya existen para ese
// empleado+período en `getLiquidacionesContadoraEmpleado`).
export const subirLiquidacionPdf = async (empleadoId, año, mes, version, file) => {
  const path = `${empleadoId}/${año}-${String(mes).padStart(2, "0")}-v${version}.pdf`;
  const { error } = await client.storage.from("liquidaciones").upload(path, file, { contentType: "application/pdf" });
  if (error) throw error;
  return path;
};

/**
 * Reemplaza atómicamente la liquidación vigente de un empleado+período (apaga la anterior + inserta
 * la nueva) vía la RPC `guardar_liquidacion_contadora` — MISMO camino que usa el script de carga
 * batch (PASO C1), nunca dos llamadas sueltas (10.202).
 * @param {object} data  El objeto `data` que devuelve `parsearLiquidacionTexto` (ok:true).
 * @param {string} archivoPath  Path ya subido a Storage (ver `subirLiquidacionPdf`).
 * @param {string} archivoNombreOriginal  Nombre del archivo tal cual lo subió el usuario (auditoría).
 * @param {number} cargadoPor  `usuarios.id` REAL del que carga (10.104 — nunca el placeholder 1
 *                             salvo que el usuario real logueado efectivamente sea Ignacio).
 */
export const guardarLiquidacionContadora = async (data, archivoPath, archivoNombreOriginal, cargadoPor) => {
  const { data: row, error } = await client.rpc("guardar_liquidacion_contadora", {
    p_empleado_id: data.empleado_id,
    p_periodo_año: data.periodo_año,
    p_periodo_mes: data.periodo_mes,
    p_archivo_path: archivoPath,
    p_archivo_nombre_original: archivoNombreOriginal,
    p_haberes: data.haberes,
    p_total_haberes: data.total_haberes,
    p_descuentos_varios: data.descuentos_varios,
    p_total_descuentos_varios: data.total_descuentos_varios,
    p_imponible_previsional: data.imponible_previsional,
    p_afp_monto: data.afp_monto,
    p_salud_tipo: data.salud_tipo,
    p_salud_monto: data.salud_monto,
    p_cesantia_monto: data.cesantia_monto,
    p_base_impuesto: data.base_impuesto,
    p_impuesto_unico_monto: data.impuesto_unico_monto,
    p_total_descuentos_legales: data.total_descuentos_legales,
    p_total_descuentos: data.total_descuentos,
    p_liquido: data.liquido,
    p_uf_mes: data.uf_mes,
    p_utm_mes: data.utm_mes,
    p_texto_crudo: data.texto_crudo,
    p_cargado_por: cargadoPor,
  });
  if (error) throw error;
  return row;
};
